import classNames from 'classnames'
import { format } from 'date-fns'
import React, { ReactNode, useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { VendorImage } from '../../components/avatar/vendorImage'
import { Button } from '../../components/button'
import { BackButton } from '../../components/button/backButton'
import { Status } from '../../components/cards/statusTag'
import Dropdown from '../../components/inputs/dropdown'
import { POSOrderReceiptModal } from '../../components/modal/app_modals/POSOrderReceiptModal'
import { OrderItem } from '../../components/product/orderItem'
import Spinner from '../../components/spinner/Spinner'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import {
  DISABLE__DELIVERY_REQUEST__FROM,
  ORDERSTATUS,
  partyMappings,
  SETTLEMENTSTATUS,
} from '../../constants'
import { formatCurrency, getTimeAgo, getVendorName } from '../../helpers'
import { usePagination, useAppSelector } from '../../hooks'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import {
  useGetHarmonizedOrdersBreakdownQuery,
  useGetOrderSettlementsByHarmonizedRefQuery,
  useRequestOrderDeliveryMutation,
  useUpdateOrderMutation,
} from '../../redux-toolkits/orders/orders.slice'
import {
  FulfilmentType,
  OrderStatus,
} from '../../redux-toolkits/orders/orders.type'
import { StatusTone } from '../../redux-toolkits/shared/shared.type'
const LineItem = ({
  title,
  value,
  className,
}: {
  title: ReactNode
  value?: ReactNode
  className?: string
}) => {
  return (
    <div
      className={classNames(
        `flex items-center justify-between gap-4 w-full`,
        className,
      )}
    >
      <div className="text-sec-black">{title}</div>

      <div className="text-black font-medium">{value}</div>
    </div>
  )
}

const OrderDetailsPage: React.FC = () => {
  const [orderDetails, setOrderDetails] = useState<{
    fulfilmentType?: FulfilmentType
    address?: string
    phoneNumber?: string
    name?: string
    byAdmin: boolean
    totalGiftCharge?: number
    totalServiceCharge?: number
    totalDeliveryCharge?: number
    totalDiscount?: number
    amount?: number
    totalOrderValue?: number
    isGift?: boolean
    giftMesssage?: string
    orderForSelf?: boolean
    recipientName?: string
    recipientContact?: string
    senderName?: string
    isPOSOrder: boolean
    tableNumber: string
  }>()
  const [lineItems, setLineItems] = useState<
    {
      title: string
      element?: ReactNode
    }[]
  >([])

  const { harmonizedReference } = useParams<{ harmonizedReference: string }>()

  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)
  const centreFilter = selectedFulfilmentCenters
    .map((centre) => centre._id)
    .join(',')
  const navigate = useNavigate()

  const { handleModalClose, handleModalOpen, setModalOpen, modal, modalOpen } =
    useModalControl()

  const settlementsPagination = usePagination()
  useEffect(() => {
    settlementsPagination.reset()
  }, [centreFilter, settlementsPagination.reset])

  const {
    data: harmonizedOrder,
    isFetching: isLoading,
    error: orderError,
    refetch,
  } = useGetHarmonizedOrdersBreakdownQuery({
    harmonizedReference: harmonizedReference ?? '',
    fulfilmentCentre: centreFilter,
  })

  const { data: settlements, isFetching: fetchingSettlements } =
    useGetOrderSettlementsByHarmonizedRefQuery(
      {
        ref: harmonizedReference ?? '',
        fulfilmentCentre: centreFilter,
        page: settlementsPagination.page,
        limit: settlementsPagination.limit,
      },
      { skip: !harmonizedReference },
    )

  const [updateOrder, { isLoading: updatingOrder }] = useUpdateOrderMutation()

  const [requestDelivery, { isLoading: requestingDelivery }] =
    useRequestOrderDeliveryMutation()

  const handleChangeStatus = async (
    id: string,
    status: OrderStatus,
  ): Promise<void> => {
    const response = await updateOrder({ id: id, body: { status: status } })

    if (response.data?.success) {
      refetch()
    }
  }

  useEffect(() => {
    if (harmonizedOrder?.data) {
      const ord = harmonizedOrder.data

      setOrderDetails({
        fulfilmentType: ord?.orders[0]?.delivery?.type,
        address:
          ord.orders[0]?.delivery.type === 'DELIVERY'
            ? ord?.orders[0]?.delivery?.address
            : ord?.orders[0]?.fulfilmentCentre?.address,
        phoneNumber: ord?.orders[0]?.personalInformation?.phone,
        name: ord?.orders[0]?.admin
          ? 'Admin'
          : `${ord?.orders[0]?.personalInformation?.firstName} ${ord?.orders[0]?.personalInformation?.lastName}`,
        byAdmin: ord?.orders[0]?.admin ? true : false,
        totalGiftCharge: ord.summary.giftCharge,
        totalServiceCharge: ord.summary.serviceCharge,
        amount: ord.summary.orderValue,
        totalOrderValue: ord.summary.orderValue,
        isGift: ord?.orders[0]?.gift ?? false,
        giftMesssage: ord?.orders[0]?.giftMessage || undefined,
        orderForSelf: ord?.orders[0]?.buyForSelf ?? false,
        recipientName: ord?.orders[0]?.buyForSelf
          ? undefined
          : `${ord?.orders[0]?.recipientsInformation?.name}`,
        recipientContact: ord?.orders[0]?.buyForSelf
          ? undefined
          : `${ord?.orders[0]?.recipientsInformation?.email} | ${ord?.orders[0]?.recipientsInformation?.phoneNumber}`,
        senderName: ord?.orders[0]?.buyForSelf
          ? undefined
          : `${ord?.orders[0]?.recipientsInformation?.senderName}`,
        isPOSOrder: ord?.orders[0]?.isPOSOrder ?? false,
        tableNumber: ord?.orders[0]?.isPOSOrder
          ? ord?.orders[0]?.tableNumber || 'N/A'
          : 'N/A',
      })
    }
  }, [harmonizedOrder])

  useEffect(() => {
    setLineItems([
      {
        title:
          orderDetails?.fulfilmentType === 'DELIVERY'
            ? 'Address:'
            : 'Fulfilment Center',
        element: orderDetails?.address,
      },
      {
        title: 'Customer phone number:',
        element: orderDetails?.phoneNumber,
      },
      {
        title: 'Fulfilment type:',
        element:
          orderDetails?.fulfilmentType === 'DELIVERY' ? 'Delivery' : 'Pickup',
      },
      {
        title: 'This order is a gift:',
        element: orderDetails?.isGift ? 'Yes' : 'No',
      },
      {
        title: 'Gift message:',
        element: orderDetails?.giftMesssage,
      },
      {
        title: 'Order for self:',
        element: orderDetails?.orderForSelf ? 'Yes' : 'No',
      },
      {
        title: 'Recipient name:',
        element: orderDetails?.recipientName,
      },
      {
        title: 'Recipient contact:',
        element: orderDetails?.recipientContact,
      },
      {
        title: 'Sender name:',
        element: orderDetails?.senderName,
      },
      {
        title: 'POS Retail Order:',
        element: orderDetails?.isPOSOrder ? 'Yes' : 'No',
      },
      {
        title: 'Table:',
        element: orderDetails?.tableNumber,
      },
      {
        title: 'Total order value:',
        element: formatCurrency(orderDetails?.totalOrderValue ?? 0),
      },
      {
        title: 'Total service charge:',
        element: formatCurrency(orderDetails?.totalServiceCharge ?? 0),
      },
      {
        title: 'Total gift charge:',
        element: formatCurrency(orderDetails?.totalGiftCharge ?? 0),
      },
    ])
  }, [orderDetails])

  return !harmonizedReference ? (
    <Navigate to="/dashboard" replace />
  ) : (
    <Layout>
      {orderError ? (
        <p className="p-6">
          This order is unavailable in your selected fulfilment centres.
        </p>
      ) : isLoading ? (
        <Spinner fullScreen={false} />
      ) : (
        <div className="flex flex-col gap-10 mb-20">
          <BackButton />

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <PageHeader
                title={`Order #${
                  harmonizedOrder?.data?.orders?.[0]?.harmonizedReference ?? ''
                }`}
                className="!mb-0"
              />

              <Button
                type="button"
                label="Request delivery"
                onClick={async () => {
                  // await requestDelivery({ id: order._id })
                }}
                loading={requestingDelivery}
                disabled={
                  requestingDelivery ||
                  harmonizedOrder?.data?.orders
                    ?.map((order) => {
                      return (
                        (ORDERSTATUS.find(
                          (status) => status.name === order.status,
                        )?.id ?? Infinity) >= DISABLE__DELIVERY_REQUEST__FROM
                      )
                    })
                    ?.some((item) => item)
                }
                primary
                size="medium"
                iconSize={20}
                className="!hidden"
              />
            </div>

            <div>
              <p className="text-sm text-sec-black">
                Order value for selected centres
              </p>
              <p className="text-sec-black font-semibold text-xl">
                {formatCurrency(orderDetails?.amount ?? 0)}
              </p>
            </div>

            <Link
              className="underline capitalize text-sec-black"
              to={
                orderDetails?.byAdmin
                  ? ''
                  : `/customers/${
                      harmonizedOrder?.data?.orders?.[0]?.userId ?? ''
                    }`
              }
            >
              Ordered by {orderDetails?.name}
            </Link>

            <div className="text-sec-black flex flex-row gap-1 items-center flex-wrap w-fit">
              <p>
                {getTimeAgo(
                  harmonizedOrder?.data?.orders?.[0]?.createdAt ?? '',
                )}{' '}
              </p>
              <p>•</p>
              <p>
                {format(
                  harmonizedOrder?.data?.orders?.[0]?.createdAt ?? '',
                  'dd MMM, yyyy h:mma',
                )}
              </p>
              <p>•</p>
              <Button
                className="!px-0 !py-0 !gap-0.5"
                icon="receipt"
                label="Print receipt"
                variant="plain"
                iconPosition="right"
                onClick={() => {
                  handleModalOpen('POSOrderReceiptModal')
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-6 max-w-[620px]">
            <p className="text-xl font-medium">Order Details</p>

            <div>
              {lineItems
                .filter((i) => i.element !== undefined)
                .map((item, idx) => {
                  return (
                    <LineItem
                      title={item.title}
                      value={item.element}
                      className="border-b py-4 first:pt-0 last:border-b-0 last:pb-0"
                      key={`${idx}-${item.title}`}
                    />
                  )
                })}
            </div>
          </div>

          <div className="">
            {harmonizedOrder?.data?.orders?.map((order) => {
              return (
                <div
                  key={order.orderNumber}
                  className="border-b py-6 first:pt-0 last:border-b-0 last:pb-0 flex flex-col gap-6"
                >
                  <p className="text-sm text-sec-black">
                    Fulfilment centre:{' '}
                    {order.fulfilmentCentre?.name ?? 'Unassigned'} · Order
                    value: {formatCurrency(order.amount)}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <p className="text-xl font-medium">
                        {`Order #${order.orderNumber ?? ''}`}
                      </p>

                      <p>•</p>

                      <Status
                        text={
                          order?.status?.toLowerCase()?.split('_')?.join(' ') ??
                          ''
                        }
                        type={
                          (ORDERSTATUS.find(
                            (status) =>
                              status.name.toLowerCase() ===
                              (order.status ?? '').toLowerCase(),
                          )?.type ?? 'warn') as StatusTone
                        }
                        className="!text-sm"
                      />

                      <Status
                        text={
                          order.vendorId?.isOffsiteCreator
                            ? 'Offsite creator'
                            : 'Onsite creator'
                        }
                        type="blue"
                        className="!text-sm"
                      />
                    </div>

                    <Dropdown
                      loading={updatingOrder || requestingDelivery}
                      menuButton={
                        <Button
                          type="button"
                          icon="ellipses"
                          className="!px-0 size-[58px] !aspect-square"
                        />
                      }
                      closeOnClick={false}
                      menuItems={[
                        {
                          name: 'Initiate Refund',
                          value: '',
                          onClick: () => {
                            navigate(
                              `/orders/${harmonizedReference}/${order._id}/initiate-refund`,
                            )
                          },
                        },
                        {
                          name: 'Request Delivery',
                          value: '',
                          onClick: async () => {
                            await requestDelivery({ id: order._id })
                          },
                          hidden:
                            (ORDERSTATUS.find(
                              (status) =>
                                status.name.toLowerCase() ===
                                order.status.toLowerCase(),
                            )?.id ?? Infinity) >=
                              DISABLE__DELIVERY_REQUEST__FROM ||
                            orderDetails?.fulfilmentType === 'PICKUP',
                        },
                        {
                          name: 'Set as Processing',
                          value: '',
                          onClick: () => {
                            handleChangeStatus(order._id, 'PROCESSING')
                          },
                          hidden: false,
                        },
                        {
                          name: 'Set as Scheduled',
                          value: '',
                          onClick: () => {
                            handleChangeStatus(order._id, 'SCHEDULED')
                          },
                          hidden: false,
                        },
                        {
                          name: 'Set as Preparing Pickup',
                          value: '',
                          onClick: () => {
                            handleChangeStatus(order._id, 'PREPARING_PICKUP')
                          },
                          hidden: orderDetails?.fulfilmentType !== 'PICKUP',
                        },
                        {
                          name: 'Set as Ready for Pickup',
                          value: '',
                          onClick: () => {
                            handleChangeStatus(order._id, 'READY_FOR_PICKUP')
                          },
                          hidden: orderDetails?.fulfilmentType !== 'PICKUP',
                        },
                        {
                          name: 'Set as Picked up',
                          value: '',
                          onClick: () => {
                            handleChangeStatus(order._id, 'PICKED_UP')
                          },
                          hidden: orderDetails?.fulfilmentType !== 'PICKUP',
                        },
                        {
                          name: 'Set as Preparing Delivery',
                          value: '',
                          onClick: () => {
                            handleChangeStatus(order._id, 'PREPARING_DELIVERY')
                          },
                          hidden: orderDetails?.fulfilmentType !== 'DELIVERY',
                        },
                        {
                          name: 'Set as Delivered',
                          value: '',
                          onClick: () => {
                            handleChangeStatus(order._id, 'DELIVERED')
                          },
                          hidden: orderDetails?.fulfilmentType !== 'DELIVERY',
                        },
                        {
                          name: 'Set as Completed',
                          value: '',
                          onClick: () => {
                            handleChangeStatus(order._id, 'COMPLETED')
                          },
                          hidden: false,
                        },
                        {
                          name: 'Set as Cancelled',
                          value: '',
                          onClick: () => {
                            handleChangeStatus(order._id, 'CANCELLED')
                          },
                          hidden: order.status !== 'PROCESSING',
                        },
                      ]}
                      menuItemClassName="border-b-0"
                      menuClassName="!w-[300px] !max-h-[208px]"
                      className="ml-auto"
                    />
                  </div>

                  <div>
                    {order.orderItems?.map((item, idx) => {
                      const key = `item-${item._id}-${idx}`

                      return <OrderItem item={item} key={key} />
                    })}
                  </div>
                </div>
              )
            })}
          </div>

          <div>
            <TableComponent
              name="Settlements"
              showName
              isEmpty={[undefined, 0].includes(settlements?.data?.docs?.length)}
              headers={[
                'Transaction Id',
                'Order Id',
                'Party',
                'Amount Settled',
                'Status',
                'Date',
              ]}
              rows={
                settlements
                  ? settlements?.data?.docs?.map((item) => {
                      return {
                        id: item._id,
                        content: [
                          item._id,
                          <Link
                            to={`/orders/${item.order._id}`}
                            className="
                          hover:text-black hover:underline
                          transition-all duration-500
                          "
                            key={`${item.order._id}-product-id`}
                          >
                            #{item.order.orderNumber}
                          </Link>,
                          item.party === 'VENDOR' ? (
                            <div
                              key={`${item._id}-vendor-party`}
                              className="flex gap-2 items-center"
                            >
                              <VendorImage img={item.vendor.logo ?? ''} />
                              <span>{getVendorName(item.vendor)}</span>
                            </div>
                          ) : (
                            (partyMappings[
                              item.party as keyof typeof partyMappings
                            ] as string)
                          ),
                          formatCurrency(item.amount),
                          <Status
                            text={item.status.toLowerCase()}
                            type={
                              (SETTLEMENTSTATUS.find(
                                (status) =>
                                  status.name.toLowerCase() ===
                                  item.status.toLowerCase(),
                              )?.type ?? 'warn') as StatusTone
                            }
                            key={`${item._id}-status`}
                          />,
                          `${format(item.createdAt, 'dd/MM/yyyy h:mma')}`,
                        ],
                      }
                    })
                  : []
              }
              loading={fetchingSettlements}
              {...settlementsPagination.tableProps}
              totalDataCount={settlements?.data?.total ?? 0}
            />
          </div>
        </div>
      )}

      <POSOrderReceiptModal
        open={modal === 'POSOrderReceiptModal' && modalOpen}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        harmonizedReference={harmonizedReference}
        fulfilmentCentre={centreFilter}
      />
    </Layout>
  )
}

export default OrderDetailsPage
